import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'

import useMaxTaxExposure from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay/useMaxTaxExposure'
import getMaxVatCents from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getMaxVatCents'
import getTlcVatInfo, {
  unavailableTlcVatInfo,
} from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getTlcVatInfo'
import useEuMaxVatExposure from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/useEuMaxVatExposure'
import isCanadianTaxCurrency from '@/buyer-portal/helpers/canadianTaxCurrency'
import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'

import type { CalculatedAdditionalCharges } from './useCalculatedAdditionalCharges'

/**
 * The one place total landed cost is derived, for auctions and offers alike.
 *
 * Everything currently reads the `WithTax` figures — buyer-facing displays
 * (FP-2498) and analytics events alike, so what we report matches what the
 * buyer was shown. The bare figures have no consumers today; they exist to
 * name the pre-tax form should something ever need it, rather than leaving the
 * next caller to fold tax in (or leave it out) on its own.
 */
export const getTlcTotals = ({
  additionalChargesTotal,
  currentBidDollars,
  msrpPrice,
  tlcTaxDollars = 0,
  units = 0,
}: {
  /**
   * total in cents, from CalculatedAdditionalCharges
   */
  additionalChargesTotal: number
  currentBidDollars: number
  /**
   * retail value in cents
   */
  msrpPrice?: number | null
  tlcTaxDollars?: number
  units?: number
}) => {
  const totalLandedCost = currentBidDollars + additionalChargesTotal / 100
  const totalLandedCostWithTax = totalLandedCost + tlcTaxDollars
  const msrpDollars = msrpPrice ? msrpPrice / 100 : null

  return {
    tlcPercentOfMsrp: msrpDollars ? totalLandedCost / msrpDollars : null,
    tlcPercentOfMsrpWithTax: msrpDollars
      ? totalLandedCostWithTax / msrpDollars
      : null,
    tlcPerUnit: units > 0 ? totalLandedCost / units : null,
    tlcPerUnitWithTax: units > 0 ? totalLandedCostWithTax / units : null,
    totalLandedCost,
    totalLandedCostWithTax,
  }
}

/**
 * `getTlcTotals` with max tax exposure fetched and folded in, for the surfaces
 * that display total landed cost. Tax suppression is derived here from the
 * listing rather than passed in, so every display agrees on when tax applies:
 * the Canadian tax currencies (CAD/USD) and the EU VAT currencies (EUR/GBP,
 * FP-3262), excluding contract listings.
 *
 * Callers that must show a pre-tax figure should read the bare outputs rather
 * than skip this hook — both exposure hooks self-gate, so an out-of-path
 * currency issues no request.
 */
export const useTlcTotals = ({
  additionalCharges,
  currency,
  listingId,
  listingType,
  msrpPrice,
  selectedQuote,
  units,
}: {
  additionalCharges: CalculatedAdditionalCharges
  currency?: string
  listingId: string
  listingType?: string | null
  /**
   * retail value in cents
   */
  msrpPrice?: number | null
  selectedQuote?: OrchestratedQuote | null
  units?: number
}) => {
  const { basis, bstockFee, total } = additionalCharges
  const shippingCents = total - bstockFee

  // Pass bid as formSingleBid + formProxyMaxBid; svc applies Reading B.
  const formAmountCents = Math.round(basis * 100) // guard float precision
  const maxTax = useMaxTaxExposure({
    buyerAddressId: selectedQuote?.destination.id,
    buyerFeeCents: bstockFee,
    formProxyMaxBid: formAmountCents,
    formSingleBid: formAmountCents,
    isTaxable: isCanadianTaxCurrency(currency) && listingType !== 'CONTRACT',
    listingId,
    shipmentType: selectedQuote?.shipmentType,
    shippingCents,
  })
  const tlcTaxInfo = maxTax?.components
    ? { components: maxTax.components, totalCents: maxTax.maxTaxAmount }
    : undefined

  // FP-3262 — same request the confirm modals make (shared react-query
  // key), so the breakdown and the modal quote one VAT figure.
  const isVatApplicable =
    isEuTaxCurrency(currency) && listingType !== 'CONTRACT'
  const maxVat = useEuMaxVatExposure({
    buyerAddressId: selectedQuote?.destination.id,
    buyerFeeCents: bstockFee,
    formProxyMaxBid: formAmountCents,
    formSingleBid: formAmountCents,
    isTaxable: isVatApplicable,
    listingId,
    shippingCents,
  })
  // FP-3264 — VAT applies by currency before an address or bid lets svc
  // estimate it; keep the row (and the VAT tooltip) rather than dropping it.
  const tlcVatInfo = isVatApplicable
    ? (getTlcVatInfo(maxVat) ?? unavailableTlcVatInfo)
    : undefined

  return {
    ...getTlcTotals({
      additionalChargesTotal: total,
      currentBidDollars: basis,
      msrpPrice,
      tlcTaxDollars:
        ((tlcTaxInfo?.totalCents ?? 0) + getMaxVatCents(maxVat)) / 100,
      units,
    }),
    shippingCents,
    tlcTaxInfo,
    tlcVatInfo,
  }
}
